import React, { useState } from 'react'
import { T, probColor } from './ui'

// Single-hue sequential heat for magnitude cells (green good) + amber for low,
// consistent with the app's palette. Value always shown too — never color alone.
export function heat(v, min, max, inv = false) {
  let t = (v - min) / (max - min)
  if (inv) t = 1 - t
  t = Math.max(0, Math.min(1, t))
  return t >= 0.5
    ? `rgba(0,217,126,${(0.05 + t * 0.28).toFixed(2)})`
    : `rgba(245,158,11,${(0.05 + (1 - t) * 0.2).toFixed(2)})`
}
export const rankColor = r => r <= 8 ? T.green : r <= 16 ? T.mint : r <= 24 ? T.amber : T.red

/**
 * DataGrid — the analytical position grid. Sortable, heat-colored, horizontally
 * scrollable (the row scrolls inside its own container; the page never does).
 * cols: [{ key, label, left?, fmt?(v,row), heat?{min,max,inv}, color?(v,row) }]
 */
export default function DataGrid({ cols, rows, initialSort, onRow, minWidth = 620 }) {
  const [sortKey, setSortKey] = useState(initialSort || cols[cols.length - 1].key)
  const [dir, setDir] = useState(-1)
  const sorted = [...rows].sort((a, b) => {
    const x = a[sortKey], y = b[sortKey]
    const xn = x == null, yn = y == null            // unboarded (null) rows always sink
    if (xn || yn) return xn === yn ? 0 : xn ? 1 : -1
    if (typeof x === 'number' && typeof y === 'number') return (y - x) * -dir
    return String(y).localeCompare(String(x)) * -dir
  })
  const click = k => { if (k === sortKey) setDir(d => -d); else { setSortKey(k); setDir(-1) } }
  return (
    <div style={{ overflowX: 'auto', border: `1px solid ${T.border}`, borderRadius: 12, background: T.card }}>
      <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12.5, minWidth }}>
        <thead>
          <tr>
            {cols.map(c => (
              <th key={c.key} onClick={() => click(c.key)} style={{
                position: 'sticky', top: 0, background: '#0E1622', color: sortKey === c.key ? T.green : T.muted,
                fontWeight: 700, textAlign: c.left ? 'left' : 'right', padding: '9px 10px',
                borderBottom: `1px solid ${T.border2}`, whiteSpace: 'nowrap', cursor: 'pointer',
                fontSize: 11, letterSpacing: '0.03em',
              }}>{c.label}{sortKey === c.key ? (dir < 0 ? ' ▾' : ' ▴') : ''}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((r, i) => (
            <tr key={i} onClick={() => onRow && onRow(r)} style={{ cursor: onRow ? 'pointer' : 'default' }}
              onMouseEnter={e => e.currentTarget.style.background = '#0E1826'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              {cols.map(c => {
                const raw = r[c.key]
                const hv = c.heatVal ? c.heatVal(r) : raw
                const bg = (c.heat && hv != null) ? heat(hv, c.heat.min, c.heat.max, c.heat.inv) : undefined
                const col = c.color ? c.color(raw, r) : T.text
                return (
                  <td key={c.key} style={{
                    padding: '8px 10px', textAlign: c.left ? 'left' : 'right',
                    borderBottom: `1px solid ${T.border}`, whiteSpace: 'nowrap',
                    fontFamily: c.left ? 'inherit' : T.mono, background: bg, color: col,
                  }}>{c.fmt ? c.fmt(raw, r) : raw}</td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// shared name cell renderer
export const nameCell = (v, r) => (
  <span><b style={{ color: T.text }}>{v}</b>{' '}
    <span style={{ color: T.muted, fontSize: 11 }}>{r.team}{r.opp ? `·${r.opp}` : ''}</span></span>
)
export const probCell = v => v == null
  ? <span style={{ opacity: .4 }}>—</span>
  : <b style={{ color: probColor((v || 0) / 100) }}>{v}%</b>
